// Portal wiring per DEV-GUIDE-002 §3.1 — one QueryClient, providers outside-in:
// QueryClientProvider → SessionProvider → CapabilitiesProvider → RouterProvider.
// Identical wiring to the admin/staff portals; the PUBLIC-AWARE difference lives
// in the router, not here:
// the SessionProvider still runs the one /me probe, but anonymous (fetchMe → null)
// is a FIRST-CLASS state the OPAC landing renders for — never an auto-redirect.
import { Suspense } from "solid-js";
import { render } from "solid-js/web";
import { QueryClient, QueryClientProvider } from "@tanstack/solid-query";
import { RouterProvider } from "@tanstack/solid-router";
import { CapabilitiesProvider, SessionProvider } from "@erp/auth-kit/fe";
import { PageSkeleton } from "@erp/ui/page-skeleton";
import { applyTheme, resolveTheme } from "@erp/ui/theme-toggle";
import { ApiError, isAuthError, unwrap } from "@erp/api-client";
import { installHumanErrorMap, type Capabilities, type Session } from "@erp/core-schemas";
import { client } from "./client";
import { GRANTS } from "./grants";
import { createAppRouter } from "./router";
import "./styles.css";

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000, // listings-tier default (ADR-001 §10.2); factories override per query
      // Never retry an auth failure into a loop (DEV-GUIDE-002 §3), and never
      // retry a 4xx at all: a 404 is the record not existing and a 403 the
      // account lacking a grant — three backoffs changed neither, they only
      // held a skeleton on screen for 2–3s before the NotFoundCard could say so
      // (audit UX-10). Only a 5xx or a network failure (no status) earns two.
      retry: (count, err) => count < 2 && !isAuthError(err) && !(err instanceof ApiError && err.status < 500),
    },
  },
});

const router = createAppRouter(queryClient);

/** The one /me call. 401 → null: anonymous is a state, not an error — and on the
 * OPAC that state is legitimate (the public catalogue renders for it). */
async function fetchMe(): Promise<Session | null> {
  try {
    return await unwrap<Session>(client.api.me.$get());
  } catch (err) {
    if (isAuthError(err)) return null;
    throw err; // transient (network/5xx) — SessionProvider retries briefly
  }
}

/**
 * The one /api/capabilities call — the install's module set and features
 * (ERP-DEPLOYMENT-MODEL §13). Public, no session needed; the provider retries
 * a failure twice and then falls back to the UNRESTRICTED default (UI sugar
 * fails open — the gateway's 404 MODULE_DISABLED is the real gate).
 */
const fetchCapabilities = () => unwrap<Capabilities>(client.api.capabilities.$get());

// THEME BEFORE THE FIRST RENDER (2026-09-16, audit B1). The header's ThemeToggle
// stamps `<html data-theme>` on mount — but only on pages that mount the Header.
// The login screen, the verify/setup flows and the scanner do not, so a dark
// user landed on them light. The attribute is the ONE activation every
// [data-theme="shippraDark"] rule keys on (base.css, tokens.css, spectrum.css),
// so it is written here, once, before anything renders; the toggle later
// re-writes the same name (a no-op) and mirrors any flip. index.html carries
// the same resolution as a pre-paint inline script so the stylesheet's first
// paint is already dark — this call is the module-graph twin of that script,
// for the case where the inline stamp was stripped (a CSP, an HTML minifier).
applyTheme(resolveTheme());

// HUMAN VALIDATION MESSAGES, once, before the first parse (2026-09-18, audit
// UX-05). Zod's defaults read like a stack trace ("Too small: expected string
// to have >=1 characters"); the shared map in @erp/core-schemas re-words every
// default issue with the field's key ("Display name is required"), and forms
// swap their real labels in through fieldErrors(). A composition-root call,
// because z.config is process-global: a module never installs it.
installHumanErrorMap();

render(
  () => (
    <QueryClientProvider client={queryClient}>
      <SessionProvider fetchMe={fetchMe} grants={GRANTS}>
        {/* Inside the session, outside the router: the shell waits for this
            manifest exactly as it waits for /me before it draws a sidebar. */}
        <CapabilitiesProvider fetchCapabilities={fetchCapabilities}>
          {/* Blank-page backstop: reading a pending solid-query result suspends,
              and with no boundary above the reader Solid renders NOTHING. */}
          <Suspense fallback={<PageSkeleton id="app-skeleton" />}>
            <RouterProvider router={router} />
          </Suspense>
        </CapabilitiesProvider>
      </SessionProvider>
    </QueryClientProvider>
  ),
  document.getElementById("root")!,
);
